'use client';

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import React, { useContext, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { Portal } from 'components/Portal';
import type { PortalProps } from 'components/Portal/Portal';
import type { TooltipPositioningProps } from 'components/Tooltip/TooltipContent';
import { useFloatingPosition } from 'hooks/useFloatingPosition/useFloatingPosition';
import { isFunction } from 'utils/is-function';
import type { VirtualElement } from '@floating-ui/dom';
import {
  mergeRefs,
  useEnterAnimation,
  useExitAnimation,
} from '@react-aria/utils';
import type { ThemeShadows } from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';
import styles from './PopoverContent.module.css';
import type { PopoverColors } from './PopoverContext';
import { PopoverContext } from './PopoverContext';
import { PopoverStatusContext } from './PopoverStatusContext';

type RenderChildArgs = {
  onRequestClose: () => void;
};

export type PopoverContentRenderChild = (args: RenderChildArgs) => ReactNode;

export type PopoverContentProps = {
  /** Provide a getBoundingClientRect position for an element. */
  anchorPosition?: VirtualElement;
  /** The Children of the Popover container. Can be an element or a render
   * callback that has access to onRequestClose. */
  children: ReactNode | PopoverContentRenderChild;
  /** Whether to use the portal */
  shouldUsePortal?: boolean;
  /** Whether the popover is showing */
  isShowing?: boolean;
  /** @deprecated Whether to observe the content size */
  observeContentSize?: boolean;
  /** @deprecated Whether to observe the target size */
  observeTargetSize?: boolean;
  /** @deprecated Whether to observe the mouse move */
  observeMouseMove?: boolean;
} & React.HTMLAttributes<HTMLElement> &
  Pick<PortalProps, 'hasOutermostTheme'> &
  Pick<
    PositionedPopoverProps,
    | 'shouldAnimate'
    | 'shouldUsePositioner'
    | 'maxWidth'
    | 'zIndex'
    | 'color'
    | 'mountDuration'
    | 'unmountDuration'
    | 'shadow'
    | 'autoHide'
    | 'hideOverflow'
  > &
  TooltipPositioningProps;

export const PopoverContent = React.forwardRef<any, PopoverContentProps>(
  function PopoverContent(
    {
      offset,
      shouldUsePortal = false,
      isShowing,
      children,
      anchorPosition,
      target,
      observeContentSize: __observeContentSize,
      observeTargetSize: __observeTargetSize,
      observeMouseMove: __observeMouseMove,
      shouldAnimate = true,
      hasOutermostTheme = false,
      shouldUsePositioner,
      ...other
    },
    ref
  ) {
    const localRef = useRef<HTMLDivElement>(null);
    const { onRequestClose } = useContext(PopoverContext);

    function renderContent(): ReactNode {
      if (isFunction(children)) {
        return children({ onRequestClose }) as ReactNode;
      }

      return children;
    }

    const isExiting = useExitAnimation(localRef, Boolean(isShowing));

    const isShowingStatusContext = useMemo(
      () => ({
        isShowing: Boolean(isShowing),
      }),
      [isShowing]
    );

    let content = null;

    if (isShowing || isExiting) {
      content = (
        <Portal
          disabled={!shouldUsePortal}
          hasOutermostTheme={hasOutermostTheme}
        >
          <PositionedContent
            ref={mergeRefs(localRef, ref)}
            isExiting={isExiting}
            offsetCrossAxis={offset?.[0]}
            offsetMainAxis={offset?.[1]}
            shouldAnimate={shouldAnimate}
            shouldUsePositioner={shouldUsePositioner}
            target={anchorPosition || target}
            {...other}
          >
            {renderContent()}
          </PositionedContent>
        </Portal>
      );
    }

    return (
      <PopoverStatusContext.Provider value={isShowingStatusContext}>
        {content}
      </PopoverStatusContext.Provider>
    );
  }
);

type PositionedPopoverProps = {
  /** Whether the popover is in the process of exiting/closing */
  isExiting: boolean;
  /** Whether to use the positioner for automatic positioning */
  shouldUsePositioner?: boolean;
  /** Whether to animate the popover entrance and exit */
  shouldAnimate?: boolean;
  /** Offset along the main axis of the popover placement */
  offsetMainAxis?: number;
  /** Offset along the cross axis of the popover placement */
  offsetCrossAxis?: number;
  /** Maximum width for the popover container */
  maxWidth?: string;
  /** The z-index of the popover */
  zIndex?: number;
  /** The size of the shadow to render. */
  shadow?: ThemeShadows;
  /** The color of the popover */
  color?: PopoverColors;
  /** The duration of the mount animation */
  mountDuration?: number;
  /** The duration of the unmount animation */
  unmountDuration?: number;
  /** Whether to hide the overflow of the popover. NOTE: this should not be used if another Popover (or component that's rendered in a Popover like Menu or Select) is nested inside of this one and is _not_ rendered in a Portal. The default for Popovers is to not be rendered in a Portal and can be adjusted with `shouldUsePortal`. */
  hideOverflow?: boolean;
} & TooltipPositioningProps &
  HTMLAttributes<HTMLDivElement>;

const PositionedContent = React.forwardRef<
  HTMLDivElement,
  PositionedPopoverProps
>(
  (
    {
      isExiting,
      shouldAnimate = true,
      children,
      placement = 'bottom',
      target,
      boundary,
      autoHide = true,
      fallbackPlacements,
      overflowPadding = 8,
      offsetCrossAxis,
      offsetMainAxis = 4,
      shouldUsePositioner = false,
      zIndex = theme.zIndices.popover,
      style,
      color: __color = 'secondary',
      mountDuration = 200,
      shadow = '12dp',
      unmountDuration = 125,
      hideOverflow = false,
      ...other
    },
    forwardedRef
  ) => {
    const localRef = useRef<HTMLDivElement>(null);

    const { placement: floatingUIPlacement, referenceHidden } =
      useFloatingPosition(target, localRef, {
        enabled: shouldUsePositioner,
        placement,
        boundary,
        autoHide,
        fallbackPlacements,
        overflowPadding,
        offsetMainAxis,
        offsetCrossAxis,
      });

    const resolvedPlacement = shouldUsePositioner
      ? floatingUIPlacement
      : placement;

    const isEntering = useEnterAnimation(
      localRef,
      Boolean(resolvedPlacement && shouldAnimate)
    );

    const { className, ...restProps } = other;

    return (
      <div
        ref={mergeRefs(localRef, forwardedRef)}
        className={clsx(styles.container, className)}
        data-entering={isEntering || undefined}
        data-exiting={isExiting || undefined}
        data-hide-overflow={hideOverflow || undefined}
        data-position={resolvedPlacement || undefined}
        data-positioner={shouldUsePositioner || undefined}
        data-reference-hidden={referenceHidden || undefined}
        style={
          {
            zIndex,
            '--vapor-popover-mount-duration': `${mountDuration}ms`,
            '--vapor-popover-unmount-duration': `${unmountDuration}ms`,
            '--vapor-popover-shadow': shadow ? theme.shadows[shadow] : 'none',
            ...style,
          } as CSSProperties
        }
        tabIndex={-1}
        {...restProps}
      >
        {children}
      </div>
    );
  }
);

